'use client'

/**
 * Letting a page tell the header what its record is called.
 *
 * The header sits in the root layout and derives its breadcrumb from the URL,
 * which is right for `/queue` and `/ai/policies` and wrong for every dynamic
 * route in the app. `/cases/b8978514-2e32-41ad-b057-7fbf93405c42` rendered as
 * **B8978514 2e32 41ad b057 7fbf93405c42**, because the path formatter
 * capitalises each segment and turns hyphens into spaces. The reviewer's own
 * name for that case is `UIC-VI-2026-0001`, it is what they paste into an email
 * and search the queue for, and it was the one string the chrome would not say.
 *
 * A context rather than a lookup in the header. The case page has already
 * fetched the application by the time it can name it, so a second fetch in the
 * header would be the same request twice and two chances to disagree. It also
 * keeps UIC domain knowledge out of a shared layout component: any page with a
 * record can call the hook, and the header stays a header.
 *
 * The label is scoped to the path that set it. Without that, navigating from a
 * case file to a page that sets no label would leave the old case number in the
 * breadcrumb for a frame or two -- a stale record identifier in the chrome, which
 * is a worse failure than the raw uuid it replaced.
 */

import * as React from 'react'
import { usePathname } from 'next/navigation'

interface BreadcrumbState {
  /** The label to render for the last path segment, when a page has set one. */
  label: string | null
  /** The path the label belongs to. A label from another path is ignored. */
  path: string | null
}

interface BreadcrumbContextValue extends BreadcrumbState {
  setLabel: (next: BreadcrumbState) => void
}

const BreadcrumbContext = React.createContext<BreadcrumbContextValue | null>(null)

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<BreadcrumbState>({
    label: null,
    path: null,
  })
  const value = React.useMemo(
    () => ({ ...state, setLabel: setState }),
    [state]
  )
  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

/**
 * The label the current page has published for its last breadcrumb segment.
 *
 * Returns null when no page has set one, or when the label belongs to a
 * different path — which is what the header falls back to the URL for.
 */
export function useBreadcrumbLabel(): string | null {
  const context = React.useContext(BreadcrumbContext)
  const pathname = usePathname()
  if (!context) return null
  if (!context.label || context.path !== pathname) return null
  return context.label
}

/**
 * Publish a name for this page's record, for as long as the page is mounted.
 *
 * Call it with `null` while the record is still loading: the header then shows
 * the path-derived label rather than a stale one from the previous page.
 *
 * ```tsx
 * useSetBreadcrumbLabel(application?.tracking_no ?? null)
 * ```
 */
export function useSetBreadcrumbLabel(label: string | null | undefined): void {
  const context = React.useContext(BreadcrumbContext)
  const pathname = usePathname()
  const setLabel = context?.setLabel

  React.useEffect(() => {
    if (!setLabel) return
    setLabel({ label: label || null, path: pathname })
    // Cleared on unmount so a page that sets no label never inherits one. The
    // path guard in the reader covers the same case during the render that
    // follows a navigation; this covers the case where nothing renders after.
    return () => setLabel({ label: null, path: null })
  }, [setLabel, label, pathname])
}
